import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { cn, formatBytes, formatDate, getStatusColor } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toast';
import type { Device, FileOperation } from '@/types';
import {
  Folder,
  File,
  FileText,
  FileCode,
  FileSpreadsheet,
  FileArchive,
  Image as ImageIcon,
  HardDrive,
  Upload,
  Download,
  FolderPlus,
  Edit2,
  Trash2,
  Copy,
  Move,
  Archive,
  Search,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Info,
  Shield,
  X,
  AlertTriangle,
  CheckCircle,
  Clock,
  MoreVertical,
  Maximize2,
  Minimize2,
  Grid,
  List as ListIcon,
  ArrowLeft,
  FileX,
} from 'lucide-react';

interface Drive {
  letter: string;
  label: string;
  totalGb: number;
  usedGb: number;
  type: 'ssd' | 'hdd' | 'nvme';
}

interface FileItem {
  name: string;
  size: number;
  modified: string;
  type: 'file' | 'folder';
  extension?: string;
  isHidden?: boolean;
  fileTypeLabel?: string;
}

interface TransferItem {
  id: string;
  name: string;
  type: 'upload' | 'download' | 'copy' | 'move' | 'zip' | 'extract';
  sizeBytes: number;
  writtenBytes?: number;
  progress: number; // 0 to 100
  status: 'in_progress' | 'completed' | 'failed' | 'cancelled';
  speedBps: number;
  startedAt: string;
}

function formatSpeed(bytesPerSec: number): string {
  if (!bytesPerSec || bytesPerSec < 1) return '—';
  return `${formatBytes(bytesPerSec)}/s`;
}

function formatEta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))}s left`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s ? `${m}m ${s}s left` : `${m}m left`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m ? `${h}h ${m}m left` : `${h}h left`;
}

function joinWinPath(dir: string, name: string): string {
  const trimmed = (dir || '').replace(/[\\/]+$/, '');
  if (!trimmed) return name;
  return `${trimmed}\\${name}`;
}

function sanitizeZipName(raw: string, fallback = 'archive.zip'): string {
  let name = (raw || '').trim().replace(/[/\\]/g, '');
  name = name.replace(/^archive\.zip/i, '');
  name = name.replace(/\.zip$/i, '').trim();
  if (!name) {
    name = fallback.replace(/\.zip$/i, '');
  }
  name = name.replace(/[^\w.\- ()\[\]]+/g, '_');
  return `${name}.zip`;
}

export function FileExplorer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Device & Drive Data
  const [device, setDevice] = useState<Device | null>(null);
  const [drives, setDrives] = useState<Drive[]>([]);
  const [selectedDrive, setSelectedDrive] = useState<string>('C:');
  const [loadingDevice, setLoadingDevice] = useState(true);

  // Directory & Files State
  const [currentPath, setCurrentPath] = useState<string>('C:\\');
  const [pathInput, setPathInput] = useState<string>('C:\\');
  const [isEditingPath, setIsPathEditing] = useState(false);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);

  // Selection & UI Filters
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Sorting
  const [sortField, setSortField] = useState<'name' | 'size' | 'modified' | 'type'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Transfers / Operations Drawer
  const [transfers, setTransfers] = useState<TransferItem[]>([]);
  const [showTransfersDrawer, setShowTransfersDrawer] = useState(true);

  // Context menu & Modals
  const [activeContextMenu, setActiveContextMenu] = useState<{ x: number; y: number; item: FileItem } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [targetRenameItem, setTargetRenameItem] = useState<FileItem | null>(null);
  const [showPropertiesModal, setShowPropertiesModal] = useState(false);
  const [targetPropertiesItem, setTargetPropertiesItem] = useState<FileItem | null>(null);
  const [showPreviewModal, setShowPropertiesPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [previewItem, setPreviewItem] = useState<FileItem | null>(null);
  const [showZipModal, setShowZipModal] = useState(false);
  const [zipName, setZipName] = useState('archive.zip');
  const [listEpoch, setListEpoch] = useState(0);

  // Path Traversal Warning banner
  const [pathWarning, setPathWarning] = useState<string | null>(null);

  // Load device and drives
  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      setLoadingDevice(true);
      try {
        if (id) {
          const d = await mockApi.getDevice(id);
          const drvs = await mockApi.getDrives(id);
          if (isMounted) {
            setDevice(d);
            setDrives(drvs.length > 0 ? drvs : [
              { letter: 'C:', label: 'System', totalGb: 0, usedGb: 0, type: 'ssd' },
            ]);
            setLoadingDevice(false);
          }
        } else {
          const res = await mockApi.getDevices({ pageSize: 1 });
          if (isMounted && res.data.length > 0) {
            setDevice(res.data[0]);
            const drvs = await mockApi.getDrives(res.data[0].id);
            setDrives(drvs);
          }
          if (isMounted) setLoadingDevice(false);
        }
      } catch (error) {
        console.error('Error loading device data:', error);
        if (isMounted) {
          setLoadingDevice(false);
        }
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, [id]);

  // Fetch files whenever currentPath changes
  useEffect(() => {
    let isMounted = true;
    async function fetchFolderFiles() {
      setLoadingFiles(true);
      setSelectedFiles(new Set());

      try {
        // Path traversal check
        if (currentPath.includes('..')) {
          setPathWarning('Path contains relative traversal (..). Path has been normalized.');
        } else {
          setPathWarning(null);
        }

        const deviceId = device?.id || id || 'dev-1';
        const fetched = await mockApi.listFiles(deviceId, currentPath);

        if (isMounted) {
          const enhanced: FileItem[] = (Array.isArray(fetched) ? fetched : []).map((f: any) => {
            const ext = f.name?.includes('.') ? f.name.split('.').pop()?.toLowerCase() : '';
            const isHidden = (f.name || '').startsWith('.') || (f.name || '').startsWith('$');
            return {
              name: f.name,
              size: f.size || 0,
              modified: f.modified || new Date().toISOString(),
              type: f.type === 'folder' ? 'folder' : 'file',
              extension: ext,
              isHidden,
              fileTypeLabel: f.type === 'folder' ? 'File Folder' : getFileTypeLabel(ext),
            };
          });

          setFiles(enhanced);
          setLoadingFiles(false);
        }
      } catch (error: any) {
        console.error('Error fetching folder files:', error);
        if (isMounted) {
          setFiles([]);
          setLoadingFiles(false);
          toast(error?.message || 'Could not list files on the agent', 'error');
        }
      }
    }

    fetchFolderFiles();
  }, [currentPath, device?.id, id, listEpoch]);

  // Close context menu on global click
  useEffect(() => {
    const handleGlobalClick = () => setActiveContextMenu(null);
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  // Fake progress only for actions that are still local UI stubs.
  useEffect(() => {
    const interval = setInterval(() => {
      setTransfers((prev) =>
        prev.map((t) => {
          if (t.status === 'in_progress' && t.type !== 'download' && t.type !== 'zip') {
            const nextProg = t.progress + Math.floor(Math.random() * 15) + 10;
            if (nextProg >= 100) {
              return { ...t, progress: 100, status: 'completed' };
            }
            return { ...t, progress: nextProg };
          }
          return t;
        })
      );
    }, 800);
    return () => clearInterval(interval);
  }, []);

  // Filtered & Sorted files list
  const processedFiles = useMemo(() => {
    let result = files.filter((f) => {
      if (!showHidden && f.isHidden) return false;
      if (searchQuery) {
        return f.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    });

    result.sort((a, b) => {
      // Folders always first
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;

      let comp = 0;
      if (sortField === 'name') comp = a.name.localeCompare(b.name);
      else if (sortField === 'size') comp = a.size - b.size;
      else if (sortField === 'modified') comp = new Date(a.modified).getTime() - new Date(b.modified).getTime();
      else if (sortField === 'type') comp = (a.fileTypeLabel || '').localeCompare(b.fileTypeLabel || '');

      return sortOrder === 'asc' ? comp : -comp;
    });

    return result;
  }, [files, showHidden, searchQuery, sortField, sortOrder]);

  // Navigation helpers
  const handleNavigateFolder = (folderName: string) => {
    const normalized = currentPath.endsWith('\\') ? currentPath : `${currentPath}\\`;
    const nextPath = `${normalized}${folderName}`;
    setCurrentPath(nextPath);
    setPathInput(nextPath);
  };

  const handleNavigateUp = () => {
    const segments = currentPath.split('\\').filter(Boolean);
    if (segments.length > 1) {
      segments.pop();
      const parent = segments.length === 1 ? `${segments[0]}\\` : segments.join('\\');
      setCurrentPath(parent);
      setPathInput(parent);
    }
  };

  const handleSelectDrive = (letter: string) => {
    setSelectedDrive(letter);
    const drivePath = `${letter}\\`;
    setCurrentPath(drivePath);
    setPathInput(drivePath);
  };

  const handleApplyPathInput = () => {
    let raw = pathInput.trim();
    if (!raw) return;

    // Normalize slashes
    raw = raw.replace(/\//g, '\\');
    if (raw.includes('..')) {
      toast('Relative path traversal detected. Path normalized.', 'warning');
      raw = raw.replace(/\\\.\./g, '');
    }

    setCurrentPath(raw);
    setIsPathEditing(false);
  };

  // Selection handlers
  const handleToggleSelect = (fileName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileName)) next.delete(fileName);
      else next.add(fileName);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === processedFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(processedFiles.map((f) => f.name)));
    }
  };

  // Context menu trigger
  const handleContextMenu = (e: React.MouseEvent, item: FileItem) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  // Actions
  const handleStartUpload = () => {
    const mockFileNames = ['network_report.pdf', 'system_dump.zip', 'install_patch.exe', 'agent_config.json'];
    const randomFile = mockFileNames[Math.floor(Math.random() * mockFileNames.length)];
    const newTransfer: TransferItem = {
      id: Math.random().toString(),
      name: randomFile,
      type: 'upload',
      sizeBytes: Math.floor(Math.random() * 50000000) + 1048576,
      progress: 0,
      status: 'in_progress',
      speedBps: 1_100_000,
      startedAt: new Date().toISOString(),
    };
    setTransfers((prev) => [newTransfer, ...prev]);
    toast(`Started uploading ${randomFile} to ${currentPath}`, 'info');
  };

  const handleStartDownload = (fileItems?: FileItem[]) => {
    const targets = fileItems || processedFiles.filter((f) => selectedFiles.has(f.name));
    if (targets.length === 0) {
      toast('Select at least one file or folder to download', 'warning');
      return;
    }

    const folders = targets.filter((item) => item.type === 'folder');
    const filesOnly = targets.filter((item) => item.type !== 'folder');
    if (folders.length > 0) {
      toast('Folder download is not supported yet. Select a file.', 'warning');
    }
    if (filesOnly.length === 0) {
      return;
    }

    const deviceId = device?.id || id || '';
    filesOnly.forEach((item) => {
      void pullRemoteFile(deviceId, item);
    });
  };

  const pullRemoteFile = async (deviceId: string, item: FileItem) => {
    const picker = (window as any).showSaveFilePicker as undefined | ((opts: any) => Promise<any>);
    if (typeof picker !== 'function') {
      toast('Use Chrome or Edge to download files (up to 25 GB)', 'error');
      return;
    }

    let writable: any = null;
    try {
      const handle = await picker({ suggestedName: item.name, startIn: 'downloads' });
      writable = await handle.createWritable();
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      toast(error?.message || 'Could not open a save location', 'error');
      return;
    }

    const transferId = `${Date.now()}-${item.name}`;
    const remotePath = joinWinPath(currentPath, item.name);
    let commandId = '';
    setTransfers((prev) => [
      {
        id: transferId,
        name: item.name,
        type: 'download',
        sizeBytes: item.size || 0,
        progress: 1,
        status: 'in_progress',
        speedBps: 0,
        writtenBytes: 0,
        startedAt: new Date().toISOString(),
      },
      ...prev,
    ]);
    toast(`Saving ${item.name} — pick a folder and keep this tab open`, 'info');

    const updateTransfer = (patch: Partial<TransferItem>) => {
      setTransfers((prev) => prev.map((t) => (t.id === transferId ? { ...t, ...patch } : t)));
    };

    try {
      const started: any = await mockApi.requestFileDownload(deviceId, remotePath);
      commandId = started?.commandId || started?.command_id || '';
      if (!commandId) {
        throw new Error('Server did not start the download');
      }
      const startedAtMs = Date.now();
      let windowBytes = 0;
      let windowAt = startedAtMs;
      let speedBps = 0;
      await mockApi.streamFileDownload(deviceId, commandId, writable, (info: { written: number; total: number; received: number }) => {
        const total = info.total || item.size || 0;
        const pct = total > 0 ? Math.min(99, Math.max(1, Math.floor((info.written / total) * 100))) : 8;
        const now = Date.now();
        const windowSec = (now - windowAt) / 1000;
        if (windowSec >= 0.15) {
          const instant = (info.written - windowBytes) / windowSec;
          speedBps = speedBps > 0 ? speedBps * 0.5 + instant * 0.5 : instant;
          windowBytes = info.written;
          windowAt = now;
        }
        const elapsed = (now - startedAtMs) / 1000;
        const avg = elapsed > 0.15 ? info.written / elapsed : speedBps;
        const shown = speedBps > 0 ? speedBps : avg;
        updateTransfer({
          progress: pct,
          sizeBytes: total || info.written,
          writtenBytes: info.written,
          speedBps: shown,
        });
      });
      await writable.close();
      writable = null;
      updateTransfer({ progress: 100, status: 'completed' });
      toast(`${item.name} saved on this PC`, 'success');
    } catch (error: any) {
      if (commandId) {
        await mockApi.cancelFileDownload?.(deviceId, commandId);
      }
      try {
        await writable?.abort?.();
      } catch {
        /* ignore */
      }
      writable = null;
      updateTransfer({ status: 'failed', progress: 0 });
      toast(error?.message || `Could not download ${item.name}`, 'error');
    }
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    const folderItem: FileItem = {
      name: newFolderName.trim(),
      size: 0,
      modified: new Date().toISOString(),
      type: 'folder',
      isHidden: newFolderName.startsWith('.'),
      fileTypeLabel: 'File Folder',
    };
    setFiles((prev) => [folderItem, ...prev]);
    setShowNewFolderModal(false);
    setNewFolderName('');
    toast(`Folder '${folderItem.name}' created successfully`, 'success');
  };

  const handleConfirmDelete = () => {
    const targets = Array.from(selectedFiles);
    setFiles((prev) => prev.filter((f) => !selectedFiles.has(f.name)));
    setSelectedFiles(new Set());
    setShowDeleteModal(false);
    toast(`Deleted ${targets.length} item(s) from ${currentPath}`, 'success');
  };

  const handleConfirmRename = () => {
    if (!targetRenameItem || !renameValue.trim()) return;
    const oldName = targetRenameItem.name;
    const newName = renameValue.trim();

    setFiles((prev) =>
      prev.map((f) => (f.name === oldName ? { ...f, name: newName } : f))
    );
    setShowRenameModal(false);
    setTargetRenameItem(null);
    setRenameValue('');
    toast(`Renamed '${oldName}' to '${newName}'`, 'success');
  };

  const handleConfirmZip = async () => {
    const deviceId = device?.id || id || '';
    const names = Array.from(selectedFiles);
    const items = names
      .map((name) => files.find((f) => f.name === name))
      .filter((item): item is FileItem => !!item);
    let archive = sanitizeZipName(zipName, items.length === 1 ? `${items[0].name}.zip` : 'archive.zip');
    setShowZipModal(false);
    const transferId = `${Date.now()}-${archive}`;
    const sizeHint = items.reduce((sum, item) => sum + (item.type === 'file' ? item.size || 0 : 0), 0);
    setTransfers((prev) => [
      {
        id: transferId,
        name: archive,
        type: 'zip',
        sizeBytes: sizeHint,
        writtenBytes: 0,
        progress: 1,
        status: 'in_progress',
        speedBps: 0,
        startedAt: new Date().toISOString(),
      },
      ...prev,
    ]);
    toast(`Zipping ${names.length} item(s) on the remote PC — keep this tab open`, 'info');

    const patchTransfer = (next: Partial<TransferItem>) => {
      setTransfers((prev) => prev.map((t) => (t.id === transferId ? { ...t, ...next } : t)));
    };

    try {
      const api = mockApi as any;
      const started = await api.zipRemoteFiles(deviceId, {
        paths: names.map((name) => joinWinPath(currentPath, name)),
        zipName: archive,
        destDir: currentPath,
      });
      const commandId = started?.commandId || started?.command_id || '';
      if (!commandId) {
        throw new Error('Server did not start the zip');
      }
      const startedAt = Date.now();
      let lastBytes = 0;
      let lastAt = startedAt;
      let speedBps = 0;
      while (Date.now() - startedAt < 12 * 60 * 60 * 1000) {
        const status: any = await api.getDeviceCommand(deviceId, commandId);
        const progress = status?.progress && typeof status.progress === 'object' ? status.progress : {};
        const total = Number(progress.bytesTotal ?? progress.bytes_total ?? sizeHint) || sizeHint;
        const done = Number(progress.bytesDone ?? progress.bytes_done ?? 0) || 0;
        const now = Date.now();
        const windowSec = (now - lastAt) / 1000;
        if (windowSec >= 0.4) {
          speedBps = (done - lastBytes) / windowSec;
          lastBytes = done;
          lastAt = now;
        }
        const pct = total > 0 ? Math.min(99, Math.max(1, Math.floor((done / total) * 100))) : 1;
        patchTransfer({
          progress: pct,
          sizeBytes: total || sizeHint,
          writtenBytes: done,
          speedBps: speedBps > 0 ? speedBps : 0,
        });
        if (status?.status === 'acknowledged') {
          if (Number(status.exitCode ?? status.exit_code ?? 0) !== 0) {
            throw new Error(status.error || 'Zip failed on the agent');
          }
          const output = String(status.output || '');
          if (/command 'zip' acknowledged/i.test(output)) {
            throw new Error('This PC is running an old agent. Rebuild the agent with the builder, install it, then zip again.');
          }
          let parsed: any = {};
          try {
            parsed = output.trim().startsWith('{') ? JSON.parse(output) : {};
          } catch {
            parsed = {};
          }
          const zipPath = parsed.zipPath || progress.zipPath || '';
          const zipSize = Number(parsed.size ?? progress.size ?? done) || done;
          if (!zipPath) {
            throw new Error('This PC is running an old agent. Rebuild the agent with the builder, install it, then zip again.');
          }
          patchTransfer({
            progress: 100,
            status: 'completed',
            speedBps: 0,
            sizeBytes: zipSize || total,
            writtenBytes: zipSize || done,
            name: parsed.zipName || archive,
          });
          toast(`${parsed.zipName || archive} created on the remote PC`, 'success');
          setListEpoch((n) => n + 1);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      throw new Error('Timed out waiting for zip to finish');
    } catch (error: any) {
      patchTransfer({ status: 'failed' });
      toast(error?.message || 'Could not zip the selected items', 'error');
    }
  };

  const handlePreviewFile = (item: FileItem) => {
    if (item.type === 'folder') {
      handleNavigateFolder(item.name);
      return;
    }
    setPreviewItem(item);
    setPreviewContent(
      `// InsightNode File Preview - ${item.name}
// Path: ${currentPath}\\${item.name}
// Size: ${formatBytes(item.size)}
// Last Modified: ${formatDate(item.modified)}

{
  "system_status": "OK",
  "device_id": "${device?.id || 'DEV-10293'}",
  "agent_version": "${device?.agentVersion || '2.4.12'}",
  "logging_enabled": true,
  "audit_trail": [
    { "timestamp": "${new Date().toISOString()}", "event": "File read by technician admin" }
  ]
}`
    );
    setShowPropertiesPreviewModal(true);
  };

  // Breadcrumb path segments calculation
  const breadcrumbSegments = useMemo(() => {
    const raw = currentPath.replace(/\//g, '\\');
    const parts = raw.split('\\').filter(Boolean);
    const segments: { label: string; fullPath: string }[] = [];

    let acc = '';
    parts.forEach((p, i) => {
      acc = i === 0 ? p : `${acc}\\${p}`;
      segments.push({ label: p, fullPath: acc });
    });
    return segments;
  }, [currentPath]);

  // Total selection size
  const totalSelectedBytes = useMemo(() => {
    return files
      .filter((f) => selectedFiles.has(f.name))
      .reduce((sum, f) => sum + f.size, 0);
  }, [files, selectedFiles]);

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] bg-bg text-text-primary overflow-hidden">
      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-panel border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-text-muted hover:text-text-primary">
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-2">
            <Folder className="h-4 w-4 text-accent" />
            <span className="font-semibold text-sm">{device?.hostname || 'Device Remote Explorer'}</span>
            <Badge variant="default" className="font-mono text-xs">
              {device?.localIp || '192.168.1.100'}
            </Badge>
          </div>

          <div className="h-4 w-px bg-border my-auto mx-1" />

          {/* Drive Selector */}
          <div className="flex items-center gap-2">
            <HardDrive className="h-3.5 w-3.5 text-text-muted" />
            <select
              value={selectedDrive}
              onChange={(e) => handleSelectDrive(e.target.value)}
              className="bg-elevated border border-border rounded px-2 py-1 text-xs text-text-primary focus:outline-none focus:border-accent font-mono cursor-pointer"
            >
              {drives.map((d) => (
                <option key={d.letter} value={d.letter}>
                  {d.letter} [{d.label}] ({d.usedGb}/{d.totalGb} GB)
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-3">
          {/* Search bar */}
          <div className="relative w-48 sm:w-64">
            <Search className="h-3.5 w-3.5 text-text-muted absolute left-2.5 top-2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search current folder..."
              className="bg-bg border border-border rounded pl-8 pr-3 py-1 text-xs text-text-primary focus:outline-none focus:border-accent w-full"
            />
          </div>

          {/* Hidden files toggle */}
          <Button
            variant={showHidden ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setShowHidden(!showHidden)}
            title="Toggle Hidden Files"
          >
            {showHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5 text-text-muted" />}
            <span className="hidden sm:inline text-xs">Hidden</span>
          </Button>

          {/* View Mode Toggle */}
          <div className="flex items-center rounded border border-border p-0.5 bg-elevated">
            <button
              onClick={() => setViewMode('list')}
              className={cn('p-1 rounded text-xs', viewMode === 'list' ? 'bg-panel text-accent' : 'text-text-muted')}
            >
              <ListIcon className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={cn('p-1 rounded text-xs', viewMode === 'grid' ? 'bg-panel text-accent' : 'text-text-muted')}
            >
              <Grid className="h-3.5 w-3.5" />
            </button>
          </div>

          <Button variant="ghost" size="sm" onClick={() => setCurrentPath(currentPath)} title="Refresh">
            <RefreshCw className={cn('h-3.5 w-3.5 text-text-muted', loadingFiles && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Path Breadcrumb & Drive Status Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-2 bg-elevated border-b border-border text-xs gap-2 shrink-0">
        <div className="flex items-center gap-2 flex-1 overflow-x-auto no-scrollbar">
          <Button variant="ghost" size="sm" onClick={handleNavigateUp} className="p-1 h-7 text-text-muted hover:text-text-primary">
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>

          {isEditingPath ? (
            <div className="flex items-center gap-2 flex-1 max-w-2xl">
              <input
                type="text"
                value={pathInput}
                onChange={(e) => setPathInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyPathInput()}
                className="bg-bg border border-accent rounded px-2 py-1 text-xs font-mono text-text-primary w-full outline-none"
                autoFocus
              />
              <Button variant="primary" size="sm" onClick={handleApplyPathInput}>
                Go
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsPathEditing(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <div
              onClick={() => setIsPathEditing(true)}
              className="flex items-center gap-1 font-mono text-text-secondary hover:text-text-primary cursor-pointer px-2 py-1 rounded bg-panel/60 border border-border hover:border-border-strong flex-1 overflow-x-auto"
            >
              {breadcrumbSegments.map((seg, idx) => (
                <div key={seg.fullPath} className="flex items-center gap-1 shrink-0">
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentPath(seg.fullPath);
                      setPathInput(seg.fullPath);
                    }}
                    className="hover:underline hover:text-accent font-semibold"
                  >
                    {seg.label}
                  </span>
                  {idx < breadcrumbSegments.length - 1 && (
                    <ChevronRight className="h-3 w-3 text-text-muted" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Drive Capacity Bar */}
        {drives.length > 0 && (
          <div className="flex items-center gap-3 shrink-0 text-2xs font-mono text-text-muted">
            {drives.map((d) => {
              const pct = Math.round((d.usedGb / d.totalGb) * 100);
              return (
                <div key={d.letter} className="flex items-center gap-1.5">
                  <span>{d.letter}</span>
                  <div className="w-16 bg-border h-1.5 rounded-full overflow-hidden">
                    <div
                      className={cn('h-full', pct > 85 ? 'bg-status-critical' : pct > 70 ? 'bg-status-warning' : 'bg-status-online')}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span>{pct}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Path Traversal Warning Banner */}
      {pathWarning && (
        <div className="bg-amber-950/30 border-b border-amber-800/40 px-4 py-1.5 flex items-center justify-between text-2xs text-amber-300 font-mono shrink-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            <span>{pathWarning}</span>
          </div>
          <button onClick={() => setPathWarning(null)} className="text-amber-400 hover:text-amber-200">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* File Operations Toolbar */}
      <div className="flex flex-wrap items-center justify-between px-4 py-2 bg-panel border-b border-border gap-2 shrink-0">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          <Button variant="ghost" size="sm" onClick={handleStartUpload} title="Upload File to current path">
            <Upload className="h-3.5 w-3.5 text-accent" />
            <span className="text-xs">Upload</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            disabled={selectedFiles.size === 0}
            onClick={() => handleStartDownload()}
            title="Download selected files"
          >
            <Download className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-xs">Download</span>
          </Button>

          <div className="h-4 w-px bg-border mx-1" />

          <Button variant="ghost" size="sm" onClick={() => setShowNewFolderModal(true)} title="Create New Folder">
            <FolderPlus className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-xs">New Folder</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            disabled={selectedFiles.size !== 1}
            onClick={() => {
              const selectedName = Array.from(selectedFiles)[0];
              const item = files.find((f) => f.name === selectedName);
              if (item) {
                setTargetRenameItem(item);
                setRenameValue(item.name);
                setShowRenameModal(true);
              }
            }}
            title="Rename selected file"
          >
            <Edit2 className="h-3.5 w-3.5 text-text-muted" />
            <span className="text-xs">Rename</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            disabled={selectedFiles.size === 0}
            onClick={() => {
              const names = Array.from(selectedFiles);
              const first = files.find((f) => f.name === names[0]);
              if (names.length === 1 && first?.type === 'folder') {
                setZipName(`${first.name}.zip`);
              } else {
                setZipName('archive.zip');
              }
              setShowZipModal(true);
            }}
            title="ZIP compress selected"
          >
            <Archive className="h-3.5 w-3.5 text-text-muted" />
            <span className="text-xs">ZIP</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            disabled={selectedFiles.size === 0}
            onClick={() => setShowDeleteModal(true)}
            className="text-status-critical hover:text-status-critical hover:bg-status-critical/10"
            title="Delete selected files"
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span className="text-xs">Delete</span>
          </Button>
        </div>

        {/* Selection summary badge */}
        <div className="text-xs text-text-muted font-mono flex items-center gap-3">
          <span>{processedFiles.length} item(s)</span>
          {selectedFiles.size > 0 && (
            <Badge variant="info">
              {selectedFiles.size} selected ({formatBytes(totalSelectedBytes)})
            </Badge>
          )}
        </div>
      </div>

      {/* Main File Table / Grid View Area */}
      <div className="flex-1 overflow-y-auto bg-bg p-4 relative" onClick={() => setActiveContextMenu(null)}>
        {loadingFiles ? (
          <div className="flex flex-col items-center justify-center h-64 text-text-muted gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-accent" />
            <span className="text-xs font-mono">Fetching file system contents...</span>
          </div>
        ) : processedFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-text-muted gap-3 border-2 border-dashed border-border rounded-lg m-4">
            <Folder className="h-10 w-10 text-border-strong" />
            <span className="text-sm font-medium">This folder is empty</span>
            <span className="text-2xs">No files or subdirectories found in {currentPath}</span>
          </div>
        ) : viewMode === 'list' ? (
          /* Table View */
          <div className="surface overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-panel border-b border-border text-text-muted font-mono select-none">
                <tr>
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedFiles.size > 0 && selectedFiles.size === processedFiles.length}
                      onChange={handleSelectAll}
                      className="rounded border-border bg-bg"
                    />
                  </th>
                  <th
                    className="p-3 cursor-pointer hover:text-text-primary"
                    onClick={() => { setSortField('name'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center gap-1">
                      <span>Name</span>
                      {sortField === 'name' && (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </div>
                  </th>
                  <th
                    className="p-3 cursor-pointer hover:text-text-primary w-28"
                    onClick={() => { setSortField('size'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center gap-1">
                      <span>Size</span>
                      {sortField === 'size' && (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </div>
                  </th>
                  <th
                    className="p-3 cursor-pointer hover:text-text-primary w-40"
                    onClick={() => { setSortField('type'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center gap-1">
                      <span>Type</span>
                      {sortField === 'type' && (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </div>
                  </th>
                  <th
                    className="p-3 cursor-pointer hover:text-text-primary w-40"
                    onClick={() => { setSortField('modified'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
                  >
                    <div className="flex items-center gap-1">
                      <span>Date Modified</span>
                      {sortField === 'modified' && (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                    </div>
                  </th>
                  <th className="p-3 w-12 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {processedFiles.map((file) => {
                  const isSelected = selectedFiles.has(file.name);
                  return (
                    <tr
                      key={file.name}
                      onDoubleClick={() => file.type === 'folder' ? handleNavigateFolder(file.name) : handlePreviewFile(file)}
                      onContextMenu={(e) => handleContextMenu(e, file)}
                      className={cn(
                        'hover:bg-hover transition-colors group cursor-pointer',
                        isSelected && 'bg-accent/10 hover:bg-accent/15'
                      )}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleToggleSelect(file.name, e as any)}
                          className="rounded border-border bg-bg"
                        />
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 font-medium">
                          {getFileIcon(file)}
                          <span className={cn('truncate max-w-md', file.isHidden && 'opacity-60 italic')}>
                            {file.name}
                          </span>
                          {file.isHidden && <Badge variant="default" className="text-2xs">Hidden</Badge>}
                        </div>
                      </td>
                      <td className="p-3 font-mono text-text-muted">
                        {file.type === 'folder' ? '—' : formatBytes(file.size)}
                      </td>
                      <td className="p-3 text-text-muted">{file.fileTypeLabel}</td>
                      <td className="p-3 font-mono text-text-muted">{formatDate(file.modified)}</td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => handleContextMenu(e, file)}
                          className="p-1 rounded hover:bg-elevated text-text-muted hover:text-text-primary"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {processedFiles.map((file) => {
              const isSelected = selectedFiles.has(file.name);
              return (
                <div
                  key={file.name}
                  onDoubleClick={() => file.type === 'folder' ? handleNavigateFolder(file.name) : handlePreviewFile(file)}
                  onContextMenu={(e) => handleContextMenu(e, file)}
                  onClick={(e) => handleToggleSelect(file.name, e)}
                  className={cn(
                    'surface p-3 flex flex-col items-center justify-between text-center gap-2 cursor-pointer hover:border-accent transition-all group relative',
                    isSelected && 'border-accent bg-accent/10'
                  )}
                >
                  <div className="p-3 rounded-full bg-elevated group-hover:scale-105 transition-transform">
                    {getFileIcon(file, 'lg')}
                  </div>

                  <span className="text-xs font-medium truncate w-full" title={file.name}>
                    {file.name}
                  </span>

                  <span className="text-2xs text-text-muted font-mono">
                    {file.type === 'folder' ? 'Folder' : formatBytes(file.size)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating Active Transfers Panel */}
      {transfers.length > 0 && showTransfersDrawer && (
        <div className="bg-panel border-t border-border p-3 shrink-0 shadow-xl">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs font-semibold">
              <RefreshCw className="h-3.5 w-3.5 text-accent animate-spin" />
              <span>Transfers & Operations ({transfers.filter((t) => t.status === 'in_progress').length} active)</span>
            </div>
            <button onClick={() => setShowTransfersDrawer(false)} className="text-text-muted hover:text-text-primary">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
            {transfers.map((t) => (
              <div key={t.id} className="surface-elevated p-2 text-xs flex flex-col gap-1.5">
                <div className="flex items-center justify-between font-mono text-2xs">
                  <span className="truncate font-semibold text-text-primary max-w-[150px]">{t.name}</span>
                  <Badge variant={t.status === 'completed' ? 'online' : t.status === 'failed' ? 'critical' : 'info'} className="uppercase">
                    {t.status}
                  </Badge>
                </div>

                <div className="w-full bg-border h-1.5 rounded-full overflow-hidden">
                  <div className="bg-accent h-full transition-all duration-300" style={{ width: `${t.progress}%` }} />
                </div>

                <div className="flex items-center justify-between text-2xs text-text-muted font-mono">
                  <span>
                    {formatBytes(t.writtenBytes ?? (t.status === 'completed' ? t.sizeBytes : 0))}
                    {t.sizeBytes > 0 ? ` / ${formatBytes(t.sizeBytes)}` : ''}
                  </span>
                  <span className="text-right">
                    {t.status === 'in_progress' ? (
                      <>
                        <span className="text-emerald-400">{formatSpeed(t.speedBps)}</span>
                        {t.sizeBytes > (t.writtenBytes ?? 0) && t.speedBps > 0 ? (
                          <span className="ml-2">{formatEta((t.sizeBytes - (t.writtenBytes ?? 0)) / t.speedBps)}</span>
                        ) : null}
                      </>
                    ) : (
                      `${t.progress}%`
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit Log Footer */}
      <div className="px-4 py-2 bg-panel border-t border-border flex items-center justify-between text-2xs text-text-muted font-mono shrink-0">
        <div className="flex items-center gap-2">
          <Shield className="h-3.5 w-3.5 text-emerald-400" />
          <span>All remote file operations are logged to the audit system for compliance.</span>
        </div>
        <span>Session: <strong className="text-text-secondary">FX-2026-9901</strong></span>
      </div>

      {/* Right Click Context Menu */}
      {activeContextMenu && (
        <div
          style={{ top: activeContextMenu.y, left: activeContextMenu.x }}
          className="fixed z-50 bg-elevated border border-border shadow-2xl rounded-md py-1 w-48 text-xs font-medium text-text-primary animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              const item = activeContextMenu.item;
              if (item.type === 'folder') handleNavigateFolder(item.name);
              else handlePreviewFile(item);
              setActiveContextMenu(null);
            }}
            className="w-full text-left px-3 py-1.5 hover:bg-hover flex items-center gap-2"
          >
            <Eye className="h-3.5 w-3.5 text-accent" />
            <span>Open / Preview</span>
          </button>

          <button
            onClick={() => {
              handleStartDownload([activeContextMenu.item]);
              setActiveContextMenu(null);
            }}
            className="w-full text-left px-3 py-1.5 hover:bg-hover flex items-center gap-2"
          >
            <Download className="h-3.5 w-3.5 text-emerald-400" />
            <span>Download File</span>
          </button>

          <div className="h-px bg-border my-1" />

          <button
            onClick={() => {
              setTargetRenameItem(activeContextMenu.item);
              setRenameValue(activeContextMenu.item.name);
              setShowRenameModal(true);
              setActiveContextMenu(null);
            }}
            className="w-full text-left px-3 py-1.5 hover:bg-hover flex items-center gap-2"
          >
            <Edit2 className="h-3.5 w-3.5 text-text-muted" />
            <span>Rename</span>
          </button>

          <button
            onClick={() => {
              setTargetPropertiesItem(activeContextMenu.item);
              setShowPropertiesModal(true);
              setActiveContextMenu(null);
            }}
            className="w-full text-left px-3 py-1.5 hover:bg-hover flex items-center gap-2"
          >
            <Info className="h-3.5 w-3.5 text-text-muted" />
            <span>Properties</span>
          </button>

          <div className="h-px bg-border my-1" />

          <button
            onClick={() => {
              setSelectedFiles(new Set([activeContextMenu.item.name]));
              setShowDeleteModal(true);
              setActiveContextMenu(null);
            }}
            className="w-full text-left px-3 py-1.5 hover:bg-hover text-status-critical flex items-center gap-2"
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span>Delete</span>
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Selected Items"
        danger
        confirmLabel="Delete Permanently"
        message={
          <div className="space-y-2">
            <p>
              Are you sure you want to delete <strong>{selectedFiles.size} item(s)</strong> from{' '}
              <code className="font-mono text-2xs bg-bg p-1 rounded">{currentPath}</code>?
            </p>
            <p className="text-2xs text-text-muted">
              This operation cannot be undone from the RMM dashboard.
            </p>
          </div>
        }
      />

      {/* New Folder Modal */}
      <Modal
        open={showNewFolderModal}
        onClose={() => setShowNewFolderModal(false)}
        title="Create New Directory"
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowNewFolderModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleCreateFolder}>
              Create Folder
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-xs">
          <label className="block text-text-muted font-medium">Directory Name:</label>
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="New Folder"
            className="bg-bg border border-border rounded px-3 py-1.5 text-xs text-text-primary w-full outline-none focus:border-accent"
            autoFocus
          />
        </div>
      </Modal>

      {/* Rename Modal */}
      <Modal
        open={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        title="Rename Item"
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowRenameModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleConfirmRename}>
              Rename
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-xs">
          <label className="block text-text-muted font-medium">New Name:</label>
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            className="bg-bg border border-border rounded px-3 py-1.5 text-xs text-text-primary w-full outline-none focus:border-accent"
            autoFocus
          />
        </div>
      </Modal>

      {/* ZIP Archive Modal */}
      <Modal
        open={showZipModal}
        onClose={() => setShowZipModal(false)}
        title="Create ZIP Compression Archive"
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowZipModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleConfirmZip}>
              Compress
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-xs">
          <p className="text-text-muted">
            Compress <strong>{selectedFiles.size} selected item(s)</strong> on the remote PC.
            Large folders (including 15 GB) stay on that PC — this can take a while.
          </p>
          <div>
            <label className="block text-text-muted font-medium mb-1">Archive File Name:</label>
            <input
              type="text"
              value={zipName}
              onChange={(e) => setZipName(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              className="bg-bg border border-border rounded px-3 py-1.5 text-xs text-text-primary w-full outline-none focus:border-accent font-mono"
            />
          </div>
        </div>
      </Modal>

      {/* File Properties Modal */}
      <Modal
        open={showPropertiesModal}
        onClose={() => setShowPropertiesModal(false)}
        title="Item Properties"
        size="sm"
      >
        {targetPropertiesItem && (
          <div className="space-y-3 text-xs font-mono">
            <div className="flex items-center gap-3 p-3 bg-elevated rounded border border-border">
              {getFileIcon(targetPropertiesItem, 'lg')}
              <div>
                <div className="font-semibold text-text-primary">{targetPropertiesItem.name}</div>
                <div className="text-2xs text-text-muted">{targetPropertiesItem.fileTypeLabel}</div>
              </div>
            </div>

            <div className="space-y-2 text-2xs text-text-secondary border-t border-border pt-2">
              <div className="flex justify-between">
                <span>Location:</span>
                <span className="text-text-primary">{currentPath}</span>
              </div>
              <div className="flex justify-between">
                <span>Size:</span>
                <span className="text-text-primary">{formatBytes(targetPropertiesItem.size)}</span>
              </div>
              <div className="flex justify-between">
                <span>Modified Date:</span>
                <span className="text-text-primary">{formatDate(targetPropertiesItem.modified)}</span>
              </div>
              <div className="flex justify-between">
                <span>Attributes:</span>
                <span className="text-text-primary">{targetPropertiesItem.isHidden ? 'Hidden, System' : 'Normal'}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* File Content Preview Modal */}
      <Modal
        open={showPreviewModal}
        onClose={() => setShowPropertiesPreviewModal(false)}
        title={`File Preview - ${previewItem?.name || ''}`}
        size="lg"
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between text-2xs text-text-muted font-mono bg-panel p-2 rounded border border-border">
            <span>Type: {previewItem?.fileTypeLabel}</span>
            <span>Size: {formatBytes(previewItem?.size || 0)}</span>
          </div>
          <pre className="bg-[#0a0a0b] p-3 rounded border border-border text-xs font-mono text-emerald-400 overflow-x-auto max-h-96">
            {previewContent}
          </pre>
        </div>
      </Modal>
    </div>
  );
}

// Helper file icon renderer
function getFileIcon(item: FileItem, size: 'sm' | 'lg' = 'sm') {
  const iconClass = size === 'lg' ? 'h-6 w-6' : 'h-4 w-4 shrink-0';

  if (item.type === 'folder') {
    return <Folder className={cn(iconClass, 'text-amber-400')} />;
  }

  const ext = item.extension;
  if (ext === 'json' || ext === 'xml' || ext === 'yaml' || ext === 'ps1' || ext === 'sh') {
    return <FileCode className={cn(iconClass, 'text-accent')} />;
  }
  if (ext === 'txt' || ext === 'log') {
    return <FileText className={cn(iconClass, 'text-emerald-400')} />;
  }
  if (ext === 'zip' || ext === 'rar' || ext === '7z' || ext === 'tar') {
    return <FileArchive className={cn(iconClass, 'text-purple-400')} />;
  }
  if (ext === 'pdf' || ext === 'docx' || ext === 'xlsx') {
    return <FileSpreadsheet className={cn(iconClass, 'text-blue-400')} />;
  }
  return <File className={cn(iconClass, 'text-text-muted')} />;
}

function getFileTypeLabel(ext?: string): string {
  if (!ext) return 'File';
  const map: Record<string, string> = {
    txt: 'Text Document',
    log: 'System Log File',
    json: 'JSON Data',
    xml: 'XML Document',
    ps1: 'PowerShell Script',
    exe: 'Application Executable',
    zip: 'ZIP Archive',
    pdf: 'PDF Document',
    png: 'PNG Image',
    jpg: 'JPEG Image',
  };
  return map[ext] || `${ext.toUpperCase()} File`;
}
